import {
  Button,
  Divider,
  Modal,
  PasswordInput,
  Select,
  Text,
} from "@mantine/core";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";
import {
  type FormEvent,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import PinInput from "src/generator/PinInput/PinInput";
import { useIsOnline, useLogout } from "src/hooks";
import { type ICredentials } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { SmallDivider } from "src/styled";
import { getUserId } from "src/utils";
import type { ComboboxItem } from "@mantine/core";

import {
  ButtonRow,
  FormsContainer,
  Row,
  RowsContainer,
} from "./AuthorizationModal.styled";

export interface UserSelectItem extends ComboboxItem {
  usertype: number;
  contactNumber?: string;
}

interface ISaveModal {
  opened: boolean;
  close: () => void;
  onSubmit: (credentials: ICredentials) => void;
  loading: boolean;
  users?: UserSelectItem[];
  logout?: boolean;
  isEntry?: boolean;
  externalAuth?: boolean;
  authorizedUserTypes?: number[];
  nonAuthorizedMessage?: string | ReactNode;
  title?: string;
  passOnly?: boolean;
}

const AuthorizationModal = ({
  opened,
  close,
  onSubmit,
  loading,
  users,
  logout,
  isEntry,
  authorizedUserTypes,
  nonAuthorizedMessage,
  title,
  passOnly,
}: ISaveModal) => {
  const [credentials, setCredentials] = useState<ICredentials>({
    pass: "",
    pin: "",
    userid: getUserId(),
  });
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
  const isOnline = useIsOnline();
  const handleLogout = useLogout();

  // TODO here change to single user the current one !!!!!!!!!!!!!
  const thisUser = users ? users.find(u => u.value === getUserId()) : [];

  useEffect(() => {
    if (opened && users) {
      const currentUser = users.find(u => u.value === getUserId());
      checkIsAuthorized(currentUser?.usertype);
      setCredentials({
        pass: "",
        pin: "",
        userid: currentUser?.value ?? null,
      });
    }
  }, [opened, users]);

  const handleChange = useCallback((key: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const checkIsAuthorized = useCallback(
    (usertype?: number) => {
      if (
        authorizedUserTypes &&
        !authorizedUserTypes.includes(usertype ?? -1)
      ) {
        setIsAuthorized(false);
      } else {
        setIsAuthorized(true);
      }
    },
    [authorizedUserTypes],
  );

  const handleSelect = useCallback(
    (userid: string | null) => {
      const usertype = users?.find(u => u.value === userid)?.usertype;
      checkIsAuthorized(usertype);
      setCredentials(prev => ({ ...prev, userid }));
    },
    [users, checkIsAuthorized, setCredentials],
  );

  const handleCancel = useCallback(async () => {
    if (logout) {
      await handleLogout();
    }
    close();
  }, [close, handleLogout, logout]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    onSubmit(credentials);
  };

  return (
    <Modal
      size="lg"
      opened={opened}
      zIndex={11}
      onClose={close}
      withCloseButton={false}
      closeOnClickOutside={!logout}
      closeOnEscape={!logout}>
      <Text>{title ?? "Authorization is required!"}</Text>
      <SmallDivider />

      <form onSubmit={handleSubmit}>
        <FormsContainer>
          <RowsContainer style={{ width: "70%" }}>
            <Row>
              <Text style={{ marginRight: "10px", width: "92px" }}>User</Text>
              <Select
                w={"80%"}
                placeholder="User"
                value={logout ? getUserId() : credentials.userid}
                onChange={handleSelect}
                withinPortal
                data={isEntry && thisUser ? [thisUser] : (users ?? ([] as any))}
                readOnly={logout}
              />
            </Row>

            <Row>
              <Text style={{ marginRight: "10px", width: "92px" }}>
                Password
              </Text>
              <PasswordInput
                w={"80%"}
                placeholder="Password"
                name="pass"
                value={credentials.pass}
                onChange={e => handleChange(e.target.name, e.target.value)}
              />
            </Row>

            {!passOnly && !logout && isOnline && (
              <Fragment>
                <Divider
                  label={<Text size="md">Or</Text>}
                  labelPosition="center"
                  style={{ width: "100%" }}
                />
                <Row>
                  <Text>PIN</Text>
                  <PinInput
                    value={credentials.pin}
                    onChange={v => handleChange("pin", v)}
                  />
                </Row>
              </Fragment>
            )}
          </RowsContainer>
          {!isAuthorized && (
            <Text c="red" mt={10}>
              {nonAuthorizedMessage}
            </Text>
          )}
          <ButtonRow>
            <Button
              size="xs"
              leftSection={<IconX size={"1rem"} />}
              mr={5}
              color="red"
              onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              loading={loading}
              type="submit"
              size="xs"
              leftSection={<IconDeviceFloppy size={"1rem"} />}
              ml={5}>
              Submit
            </Button>
          </ButtonRow>
        </FormsContainer>
      </form>
    </Modal>
  );
};

export default AuthorizationModal;
